import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PageArea from '../shared/PageArea';
import PageTitle from '../shared/PageTitle';
import LoadingWheel from '../shared/LoadingWheel';
import { AdminClient, AuditLogDto } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import { useTranslation } from 'react-i18next';

const pageSize = 10;

interface FilterState {
  entityName: string;
  entityId: string;
  operation: string;
}

const defaultFilters: FilterState = {
  entityName: '',
  entityId: '',
  operation: '',
};

interface AuditChangeRow {
  field: string;
  oldValue?: unknown;
  newValue?: unknown;
}

const parseChanges = (raw?: string): AuditChangeRow[] => {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, { OldValue?: unknown; NewValue?: unknown }>;
    return Object.entries(parsed).map(([field, value]) => ({
      field,
      oldValue: value?.OldValue,
      newValue: value?.NewValue,
    }));
  } catch {
    return [];
  }
};

const AuditLogPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const adminClient = useMemo(() => new AdminClient(getApiBaseUrl(), authenticatedFetch), []);

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(defaultFilters);
  const [logs, setLogs] = useState<AuditLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const loadAuditLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await adminClient.getApiAdminAuditLog(
        null,
        null,
        null,
        appliedFilters.entityName || undefined,
        appliedFilters.entityId || undefined,
        appliedFilters.operation || undefined,
        pageNumber,
        pageSize
      );

      setLogs(response.logs ?? []);
      setTotalPages(Math.max(response.totalPages ?? 1, 1));
      setTotalCount(response.totalCount ?? 0);
    } catch (err) {
      const fallback = t('auditLog.errors.load');
      const message = err instanceof Error ? err.message : fallback;
      setError(message || fallback);
      setLogs([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [adminClient, appliedFilters, pageNumber, t]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPageNumber(1);
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPageNumber(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPageNumber(value);
  };

  const operationOptions: Array<'Added' | 'Modified' | 'Deleted'> = ['Added', 'Modified', 'Deleted'];

  const formatTimestamp = (value?: Date) => {
    if (!value) {
      return t('auditLog.values.notAvailable');
    }

    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  };

  const formatChangeValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return t('auditLog.values.notAvailable');
    }

    if (typeof value === 'string' && value.trim().length === 0) {
      return t('auditLog.values.empty');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const getOperationChipColor = (operation?: string) => {
    switch (operation) {
      case 'Added':
        return 'success';
      case 'Modified':
        return 'warning';
      case 'Deleted':
        return 'error';
      default:
        return 'default';
    }
  };

  const getOperationLabel = (operation?: string) => {
    if (!operation) {
      return t('auditLog.operations.default');
    }

    return t(`auditLog.operations.${operation}`);
  };

  return (
    <PageArea>
      <PageTitle title={t('auditLog.title')} subtitle={t('auditLog.subtitle')} />
      <Card sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
          <TextField
            label={t('auditLog.filters.entity')}
            placeholder={t('auditLog.filters.entityPlaceholder') ?? undefined}
            value={filters.entityName}
            onChange={(event) => setFilters((prev) => ({ ...prev, entityName: event.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
            fullWidth
          />
          <TextField
            label={t('auditLog.filters.entityId')}
            placeholder={t('auditLog.filters.entityIdPlaceholder') ?? undefined}
            value={filters.entityId}
            onChange={(event) => setFilters((prev) => ({ ...prev, entityId: event.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
            fullWidth
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <TextField
            select
            label={t('auditLog.filters.operation')}
            value={filters.operation}
            onChange={(event) => setFilters((prev) => ({ ...prev, operation: event.target.value }))}
            fullWidth
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {operationOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {t(`auditLog.operations.${option}`)}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2} sx={{ minWidth: 'fit-content' }}>
            <Button variant="primary" size="lg" onClick={handleApplyFilters}>
              {t('auditLog.filters.apply')}
            </Button>
            <Button variant="secondary" size="lg" onClick={handleResetFilters}>
              {t('auditLog.filters.reset')}
            </Button>
          </Stack>
        </Stack>
      </Card>

      {error && (
        <Alert variant='filled' severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <LoadingWheel />
      ) : logs.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="textMD" color="text.secondary">
            {t('auditLog.empty')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3} sx={{ flex: 1 }}>
          <Stack spacing={2}>
            {logs.map((log) => {
              const changes = parseChanges(log.changes);
              const timestamp = log.timestamp ? new Date(log.timestamp) : undefined;
              const cardKey = log.id ?? `${log.entityName}-${log.entityId}-${timestamp?.toISOString() ?? 'fallback'}`;

              return (
                <Accordion key={cardKey} disableGutters elevation={1} sx={{ '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Grid container spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="textXL" sx={{ fontWeight: 600 }}>
                            {log.entityName || t('auditLog.labels.unknownEntity')}
                          </Typography>
                          <Typography
                            variant="textXS"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', wordBreak: 'break-all', display: 'block' }}
                          >
                            {log.entityId}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Chip
                          label={getOperationLabel(log.operation)}
                          color={getOperationChipColor(log.operation)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="textSM" color="text.secondary">
                          {formatTimestamp(timestamp)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="textSM" color="text.secondary">
                          {log.userName || t('auditLog.userUnknown')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Divider sx={{ mb: 2 }} />
                    {changes.length === 0 ? (
                      <Typography variant="textSM" color="text.secondary">
                        {t('auditLog.noChanges')}
                      </Typography>
                    ) : (
                      <Stack spacing={2}>
                        {changes.map((change) => (
                          <Box key={`${log.id}-${change.field}`}>
                            <Typography variant="textSM" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                              {change.field}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Box
                                  sx={{
                                    height: '100%',
                                    borderRadius: 2,
                                    backgroundColor: 'grey.50',
                                    p: 2,
                                  }}
                                >
                                  <Typography variant="textXS" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                    {t('auditLog.change.old')}
                                  </Typography>
                                  <Typography variant="textSM" sx={{ wordBreak: 'break-word', display: 'block' }}>
                                    {formatChangeValue(change.oldValue)}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Box
                                  sx={{
                                    height: '100%',
                                    borderRadius: 2,
                                    backgroundColor: (theme) => theme.palette.success.light,
                                    color: (theme) => theme.palette.success.contrastText,
                                    p: 2,
                                  }}
                                >
                                  <Typography variant="textXS" display="block" sx={{ mb: 0.5, opacity: 0.9 }}>
                                    {t('auditLog.change.new')}
                                  </Typography>
                                  <Typography variant="textSM" sx={{ wordBreak: 'break-word', display: 'block' }}>
                                    {formatChangeValue(change.newValue)}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Pagination count={totalPages} page={pageNumber} onChange={handlePageChange} color="primary" />
            <Typography variant="textSM" color="text.secondary">
              {t('auditLog.pagination.summary', { count: totalCount })}
            </Typography>
          </Stack>
        </Stack>
      )}
    </PageArea>
  );
};

export default AuditLogPage;
